import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import medicinalPlantsService from '../../services/medicinalPlantsService';
import PlantMolecularTab from '../molecular/PlantMolecularTab';
import PlantCookingTab from '../culinary/PlantCookingTab';

/**
 * PlantDetail — Códice Botánico specimen sheet.
 *
 * A medicinal-plant detail modal styled as an apothecary specimen folio:
 * fret crest, the scientific binomial set in mono italic, codex tabs, and
 * botanical tokens for compounds and names. Data contract, label helpers,
 * and the cooking/molecular tabs are preserved from the original.
 */
const PlantDetail = ({ plantId, onClose }) => {
  const [plant, setPlant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (plantId) fetchPlantDetail();
  }, [plantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlantDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await medicinalPlantsService.getPlantById(plantId);
      setPlant(data);
    } catch (err) {
      console.error('Error fetching plant detail:', err);
      setError('Error al cargar los detalles de la planta.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => ({
    DIGESTIVE: 'Digestiva', RESPIRATORY: 'Respiratoria', CALMING: 'Calmante',
    METABOLIC: 'Metabólica', ANTI_INFLAMMATORY: 'Antiinflamatoria', SKIN_HAIR: 'Piel y Cabello',
    PAIN_RELIEF: 'Alivio del Dolor', IMMUNE_SUPPORT: 'Apoyo Inmune', FEMALE_HEALTH: 'Salud Femenina',
    MEMORY_COGNITION: 'Memoria y Cognición', URINARY: 'Urinaria', CIRCULATORY: 'Circulatoria', OTHER: 'Otra',
  }[category] || category);

  const getEvidenceLabel = (level) => ({
    STRONG_EVIDENCE: 'Evidencia Científica Fuerte',
    MODERATE_EVIDENCE: 'Evidencia Científica Moderada',
    TRADITIONAL_ONLY: 'Solo Uso Tradicional Documentado',
  }[level] || level);

  const getSafetyLabel = (level) => ({
    VERY_SAFE: 'Muy Segura', SAFE: 'Segura con dosis adecuadas',
    MODERATE: 'Seguridad moderada — Precauciones necesarias',
  }[level] || level);

  const getPreparationTypeLabel = (type) => ({
    TEA: 'Té/Infusión', DECOCTION: 'Cocimiento/Decocción', TINCTURE: 'Tintura',
    POULTICE: 'Cataplasma', SYRUP: 'Jarabe', EXTRACT: 'Extracto', FRESH: 'Fresco/Directo',
    POWDER: 'Polvo', BATH: 'Baño', INHALATION: 'Inhalación/Vapor', TOPICAL: 'Uso Tópico/Externo',
    JUICE: 'Jugo', CAPSULE: 'Cápsulas', COMPRESS: 'Compresas',
  }[type] || type);

  const TABS = [
    { id: 'general', label: 'Información' },
    { id: 'uses', label: 'Usos y preparación' },
    { id: 'safety', label: 'Seguridad' },
    { id: 'cooking', label: 'Cómo cocinarla' },
    { id: 'molecular', label: 'Estructura molecular' },
  ];

  const Overlay = ({ children }) => (
    <div style={styles.overlay} onClick={onClose} className="cdx-scroll">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        style={styles.modal}
        className="cdx-grain"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
    </div>
  );

  if (loading) {
    return (
      <AnimatePresence>
        <Overlay>
          <div className="cdx-greca--gold cdx-greca" />
          <div style={styles.stateBox}>
            <div style={styles.spinner} />
            <p style={styles.stateText}>Consultando el herbario…</p>
          </div>
        </Overlay>
      </AnimatePresence>
    );
  }

  if (error || !plant) {
    return (
      <AnimatePresence>
        <Overlay>
          <div className="cdx-greca--gold cdx-greca" />
          <div style={styles.stateBox}>
            <p style={{ ...styles.stateText, color: 'var(--cdx-jamaica)' }}>
              {error || 'Planta no encontrada'}
            </p>
            <button className="cdx-btn cdx-btn--ghost" style={{ padding: '10px 22px' }} onClick={onClose}>
              Cerrar
            </button>
          </div>
        </Overlay>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <Overlay>
        {/* Fret crest */}
        <div className="cdx-greca--gold cdx-greca" style={{ position: 'relative', zIndex: 2 }} />

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <span className="cdx-kicker" style={{ color: 'var(--cdx-cempasuchil)' }}>
              Espécimen · {getCategoryLabel(plant.primary_category)}
            </span>
            <h2 style={styles.scientificName} className="cdx-binomial">
              {plant.scientific_name}
            </h2>
            {plant.popular_names?.length > 0 && (
              <p style={styles.commonName}>{plant.popular_names[0]}</p>
            )}
            {plant.botanical_family && (
              <p style={styles.botanicalFamily}>Familia · {plant.botanical_family}</p>
            )}
          </div>
          <button style={styles.closeButton} onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        {plant.main_image_url && (
          <div style={styles.imageContainer}>
            <img src={plant.main_image_url} alt={plant.scientific_name} style={styles.mainImage} />
          </div>
        )}

        {/* Tabs */}
        <div style={styles.tabs} className="cdx-scroll">
          {TABS.map((t) => (
            <button
              key={t.id}
              style={{ ...styles.tab, ...(activeTab === t.id ? styles.activeTab : {}) }}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={styles.content} className="cdx-scroll">
          {activeTab === 'general' && (
            <div className="cdx-rise">
              {plant.popular_names?.length > 0 && (
                <Section title="Nombres comunes">
                  <div style={styles.tokenRow}>
                    {plant.popular_names.map((n, i) => <span key={i} className="cdx-tag">{n}</span>)}
                  </div>
                </Section>
              )}
              {plant.indigenous_names && Object.keys(plant.indigenous_names).length > 0 && (
                <Section title="Nombres indígenas">
                  <ul style={styles.list}>
                    {Object.entries(plant.indigenous_names).map(([lang, name]) => (
                      <li key={lang}><strong>{lang}:</strong> {name}</li>
                    ))}
                  </ul>
                </Section>
              )}
              {plant.plant_description && (
                <Section title="Descripción botánica">
                  <p style={styles.text}>{plant.plant_description}</p>
                </Section>
              )}
              {plant.states_found?.length > 0 && (
                <Section title="Distribución en México">
                  <p style={styles.text}>{plant.states_found.join(' · ')}</p>
                </Section>
              )}
              <Section title="Evidencia científica">
                <span className="cdx-tag cdx-tag--turq">📊 {getEvidenceLabel(plant.evidence_level)}</span>
                {plant.clinical_studies_count > 0 && (
                  <p style={styles.metaNote}>{plant.clinical_studies_count} estudios clínicos documentados</p>
                )}
              </Section>
              {plant.active_compounds?.length > 0 && (
                <Section title="Compuestos activos">
                  <div style={styles.tokenRow}>
                    {plant.active_compounds.map((c, i) => (
                      <span key={i} className="cdx-tag cdx-tag--turq">{c}</span>
                    ))}
                  </div>
                </Section>
              )}
              {plant.historical_notes && (
                <Section title="Notas históricas">
                  <p style={styles.text}>{plant.historical_notes}</p>
                </Section>
              )}
            </div>
          )}

          {activeTab === 'uses' && (
            <div className="cdx-rise">
              {plant.traditional_uses?.length > 0 && (
                <Section title="Usos tradicionales">
                  <ul style={styles.list}>
                    {plant.traditional_uses.map((u, i) => <li key={i}>{u}</li>)}
                  </ul>
                </Section>
              )}
              {plant.preparation_methods?.length > 0 && (
                <Section title="Métodos de preparación">
                  {plant.preparation_methods.map((m, i) => (
                    <div key={i} style={styles.prepCard}>
                      <h4 style={styles.prepTitle}>{getPreparationTypeLabel(m.type)}</h4>
                      {m.dosage && <p style={styles.prepDetail}><strong>Dosis:</strong> {m.dosage}</p>}
                      {m.preparation && <p style={styles.prepDetail}><strong>Preparación:</strong> {m.preparation}</p>}
                      {m.frequency && <p style={styles.prepDetail}><strong>Frecuencia:</strong> {m.frequency}</p>}
                      {m.duration && <p style={styles.prepDetail}><strong>Duración:</strong> {m.duration}</p>}
                    </div>
                  ))}
                </Section>
              )}
              {plant.where_to_find?.length > 0 && (
                <Section title="Dónde conseguirla">
                  <div style={styles.tokenRow}>
                    {plant.where_to_find.map((p, i) => <span key={i} className="cdx-tag cdx-tag--cempa">{p}</span>)}
                  </div>
                  {plant.approximate_price_range && (
                    <p style={styles.metaNote}>Precio aproximado · {plant.approximate_price_range}</p>
                  )}
                </Section>
              )}
            </div>
          )}

          {activeTab === 'safety' && (
            <div className="cdx-rise">
              <Section title="Nivel de seguridad">
                <span className="cdx-tag cdx-tag--cempa">{getSafetyLabel(plant.safety_level)}</span>
              </Section>
              {plant.precautions?.length > 0 && (
                <Section title="⚠ Precauciones">
                  <ul style={styles.warnList}>{plant.precautions.map((p, i) => <li key={i}>{p}</li>)}</ul>
                </Section>
              )}
              {plant.contraindications?.length > 0 && (
                <Section title="🚫 Contraindicaciones">
                  <ul style={styles.warnList}>{plant.contraindications.map((c, i) => <li key={i}>{c}</li>)}</ul>
                </Section>
              )}
              {plant.drug_interactions?.length > 0 && (
                <Section title="Interacciones medicamentosas">
                  <ul style={styles.warnList}>{plant.drug_interactions.map((d, i) => <li key={i}>{d}</li>)}</ul>
                </Section>
              )}
              <Section title="Poblaciones especiales">
                <div style={styles.popGrid}>
                  <PopItem ok={plant.safe_in_pregnancy} label="Embarazo" />
                  <PopItem ok={plant.safe_in_lactation} label="Lactancia" />
                  <PopItem
                    ok={plant.safe_for_children}
                    label={`Niños${plant.minimum_age_years ? ` (≥ ${plant.minimum_age_years} años)` : ''}`}
                  />
                </div>
              </Section>
              <div style={styles.disclaimer}>
                <strong>IMPORTANTE.</strong> Esta información es educativa. Consulta siempre a un
                profesional de la salud antes de usar cualquier planta medicinal, especialmente
                en embarazo, lactancia, condiciones preexistentes o si tomas medicamentos.
              </div>
            </div>
          )}

          {activeTab === 'cooking' && <div className="cdx-rise"><PlantCookingTab plantId={plant.id} /></div>}
          {activeTab === 'molecular' && <div className="cdx-rise"><PlantMolecularTab plantId={plant.id} /></div>}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <span>Fuente · {plant.source || 'UNAM — Atlas de Plantas Medicinales'}</span>
          {plant.view_count > 0 && <span>👁 {plant.view_count}</span>}
        </div>
      </Overlay>
    </AnimatePresence>
  );
};

const Section = ({ title, children }) => (
  <div style={styles.section}>
    <h3 style={styles.sectionTitle}>{title}</h3>
    {children}
  </div>
);

const PopItem = ({ ok, label }) => (
  <div style={{ ...styles.popItem, borderColor: ok ? 'var(--cdx-jade-bright)' : 'var(--cdx-jamaica-soft)' }}>
    <span style={{ fontSize: 18 }}>{ok ? '✅' : '⛔'}</span>
    <span style={styles.popLabel}>{label}<br /><strong style={{ color: ok ? 'var(--cdx-jade)' : 'var(--cdx-jamaica)' }}>
      {ok ? 'Segura' : 'No recomendada'}</strong></span>
  </div>
);

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(22, 16, 6, 0.62)',
    backdropFilter: 'blur(3px)',
    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
    zIndex: 1000, padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 20px)', overflowY: 'auto',
  },
  modal: {
    position: 'relative',
    background: 'var(--cdx-paper)',
    borderRadius: 'var(--cdx-r-xl)',
    maxWidth: 880, width: '100%', maxHeight: '88vh',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
    boxShadow: 'var(--cdx-shadow-lg)',
    border: '1px solid var(--cdx-paper-edge)',
    fontFamily: 'var(--cdx-font-body)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '20px 28px 18px',
    borderBottom: '1px solid var(--cdx-paper-edge)',
    position: 'relative', zIndex: 2,
  },
  headerContent: { flex: 1 },
  scientificName: { margin: '6px 0 4px', fontSize: 27, fontWeight: 500, color: 'var(--cdx-jade-deep)' },
  commonName: {
    margin: '0 0 4px', fontFamily: 'var(--cdx-font-display)', fontSize: 20,
    fontWeight: 600, color: 'var(--cdx-ink)',
  },
  botanicalFamily: { margin: 0, fontSize: 13, color: 'var(--cdx-ink-faint)', fontFamily: 'var(--cdx-font-mono)' },
  closeButton: {
    background: 'var(--cdx-paper-2)', border: '1px solid var(--cdx-paper-edge)',
    width: 34, height: 34, borderRadius: 9, fontSize: 16, cursor: 'pointer',
    color: 'var(--cdx-ink-soft)', flexShrink: 0,
  },
  imageContainer: { width: '100%', maxHeight: 280, overflow: 'hidden', background: 'var(--cdx-paper-3)' },
  mainImage: { width: '100%', height: 'auto', objectFit: 'cover' },
  tabs: {
    display: 'flex', gap: 4, padding: '0 20px',
    borderBottom: '1px solid var(--cdx-paper-edge)',
    background: 'var(--cdx-paper-2)',
    overflowX: 'auto', position: 'relative', zIndex: 2,
  },
  tab: {
    padding: '13px 16px', fontSize: 13.5, fontWeight: 600,
    color: 'var(--cdx-ink-faint)', background: 'transparent', border: 'none',
    cursor: 'pointer', borderBottom: '3px solid transparent', whiteSpace: 'nowrap',
    fontFamily: 'var(--cdx-font-body)', transition: 'all 0.18s',
  },
  activeTab: { color: 'var(--cdx-jade)', borderBottomColor: 'var(--cdx-cempasuchil)' },
  content: { flex: 1, overflowY: 'auto', padding: '24px 28px', position: 'relative', zIndex: 1 },
  section: { marginBottom: 26 },
  sectionTitle: {
    margin: '0 0 12px', fontFamily: 'var(--cdx-font-display)', fontSize: 18,
    fontWeight: 600, color: 'var(--cdx-jade-deep)',
  },
  text: { margin: 0, fontSize: 14.5, lineHeight: 1.7, color: 'var(--cdx-ink-soft)' },
  metaNote: { margin: '10px 0 0', fontSize: 12.5, color: 'var(--cdx-ink-faint)', fontFamily: 'var(--cdx-font-mono)' },
  tokenRow: { display: 'flex', flexWrap: 'wrap', gap: 7 },
  list: { margin: 0, paddingLeft: 20, fontSize: 14.5, lineHeight: 1.8, color: 'var(--cdx-ink-soft)' },
  warnList: { margin: 0, paddingLeft: 20, fontSize: 14.5, lineHeight: 1.8, color: 'var(--cdx-jamaica)' },
  prepCard: {
    padding: 16, background: 'var(--cdx-paper-2)', borderRadius: 'var(--cdx-r-md)',
    marginBottom: 12, borderLeft: '3px solid var(--cdx-jade-bright)', border: '1px solid var(--cdx-paper-edge)',
  },
  prepTitle: { margin: '0 0 10px', fontSize: 16, fontWeight: 600, color: 'var(--cdx-jade)', fontFamily: 'var(--cdx-font-display)' },
  prepDetail: { margin: '6px 0', fontSize: 13.5, lineHeight: 1.55, color: 'var(--cdx-ink-soft)' },
  popGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 10 },
  popItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
    background: 'var(--cdx-paper-2)', border: '1px solid', borderRadius: 'var(--cdx-r-md)',
  },
  popLabel: { fontSize: 12.5, color: 'var(--cdx-ink-soft)', lineHeight: 1.35 },
  disclaimer: {
    padding: 16, background: 'var(--cdx-cempa-wash)',
    border: '1px solid var(--cdx-cempasuchil)', borderRadius: 'var(--cdx-r-md)',
    fontSize: 13, lineHeight: 1.6, color: '#6B4310',
  },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 28px', borderTop: '1px solid var(--cdx-paper-edge)',
    background: 'var(--cdx-paper-2)', fontSize: 12,
    color: 'var(--cdx-ink-faint)', fontFamily: 'var(--cdx-font-mono)',
    position: 'relative', zIndex: 2,
  },
  stateBox: { padding: '60px 30px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 },
  stateText: { fontSize: 15, color: 'var(--cdx-ink-soft)', margin: 0, fontFamily: 'var(--cdx-font-display)' },
  spinner: {
    width: 40, height: 40, borderRadius: '50%',
    border: '3px solid var(--cdx-paper-3)', borderTopColor: 'var(--cdx-jade)',
    animation: 'spin 0.9s linear infinite',
  },
};

export default PlantDetail;
